<template>
  <!-- Dialog 1: Select Platforms -->
  <v-dialog v-model="step1" max-width="640" persistent scrollable>
    <v-card class="pa-4 position-relative">
      <v-progress-linear v-if="loading" indeterminate color="#107569" height="3" absolute top></v-progress-linear>

      <v-card-title class="d-flex justify-space-between align-center">
        <div class="text-h6 font-weight-bold">
          Select Platforms and Accounts
        </div>
      </v-card-title>

      <v-card-subtitle class="text-caption mb-3">
        Your campaign will be published across all selected platforms.
        <br />
        Choose one account per platform.
      </v-card-subtitle>

      <v-divider class="mb-2"></v-divider>

      <v-card-text>
        <v-expansion-panels v-model="activePanels" multiple>
          <v-expansion-panel v-for="(platform, index) in platforms" :key="platform.name">
            <v-expansion-panel-title>
              <div class="d-flex align-center">
                <v-avatar size="24" class="mr-2" v-if="platform.icon">
                  <v-img :src="platform.icon" />
                </v-avatar>
                <span class="text-subtitle-2 font-weight-medium">
                  {{ platform.label }}
                </span>
              </div>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <div class="as">
                <v-radio-group v-model="selectedAccounts[platform.name]" density="compact" hide-details>
                  <v-radio v-for="account in platform.accounts" :key="account.id" :value="{
                    id: account.id,
                    name: account.name,
                    avatar: account.avatar,
                  }" :color="'#107569'" class="my-2">
                    <template #label>
                      <div class="d-flex align-center">
                        <v-avatar size="36" class="mx-2">
                          <v-img :src="account.avatar" />
                        </v-avatar>
                        <span>{{ account.name }}</span>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-divider class="my-2"></v-divider>

      <v-card-actions class="d-flex justify-end">
        <v-btn v-if="hasSelectedAccounts" color="#F2F2F2" style="color: #4D4D4D;" class="con" variant="flat"
          @click="clearaccount">
          Clear
        </v-btn>
        <v-btn color="#237276" class="con" variant="flat" @click="goToStep2" :disabled="!hasSelectedAccounts">
          Continue
        </v-btn>

      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Dialog 2: Choose Objective -->
  <v-dialog v-model="step2" max-width="700" persistent>
    <v-card class="pa-6">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="text-h6 font-weight-bold">Choose Objective</div>
      </v-card-title>

      <v-card-text class="d-flex">
        <v-radio-group v-model="selectedObjective" class="mr-6">
          <v-radio v-for="item in objectives" :key="item.value" :value="item.value" :color="'#107569'" class="mb-4">
            <template #label>
              <div class="d-flex align-center ga-4">
                <v-avatar size="40">
                  <v-img :src="item.icon" />
                </v-avatar>
                <span>{{ item.label }}</span>
              </div>
            </template>
          </v-radio>
        </v-radio-group>

        <div v-if="currentObjective" style="max-width: 350px">
          <v-img :src="currentObjective.image" width="90" class="mb-4" />
          <h4 class="text-subtitle-1 font-weight-bold">
            {{ currentObjective.label }}
          </h4>
          <p class="text-body-2 mb-2">{{ currentObjective.desc }}</p>
          <div class="d-flex flex-wrap ga-2 mt-1">
            <p class="text-subtitle-1 font-weight-bold mb-1">Good for</p>
            <div class="d-flex flex-wrap ga-2 mt-1">
              <v-chip v-for="tag in currentObjective.tags" :key="tag" color="grey-lighten-4"
                class="text-body-2 font-weight-medium px-4 py-2" variant="flat" size="default" label>
                {{ tag }}
              </v-chip>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="d-flex justify-end mt-4">
        <v-btn color="#237276" class="con" variant="flat" @click="finalSubmit">
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import apiClient from "@/core/buildRequest";
import { useSuperAddStore } from "@/store/superAddStore";
export default {
  name: "PlatformSelector",
  data() {
    return {
      step1: true,
      step2: false,
      loading: true,
      selectedAccounts: {},
      platforms: [],
      store: useSuperAddStore(),
      activePanels: [0],
      selectedObjective: "awareness",
      objectives: [
        {
          value: "awareness",
          label: "Awareness",
          desc: "Show your ads to people who are most likely to remember them.",
          icon: require("@/components/SuperApp/DialogImg/Awar.svg"),
          image: require("@/components/SuperApp/DialogImg/right-awar.svg"),
          tags: [
            "Reach",
            "Brand awareness",
            "Video views",
            "Store location awareness",
            "Place Promotion",
            "Ad Engagement",
          ],
        },
        {
          value: "leads",
          label: "Leads",
          desc: "Drive more leads by encouraging people to submit forms.",
          icon: require("@/components/SuperApp/DialogImg/leads.svg"),
          image: require("@/components/SuperApp/DialogImg/leads.svg"),
          tags: ["Form submission", "Lead generation", "Calls"],
        },
        {
          value: "video_views",
          label: "Video views",
          desc: "Optimize delivery to people likely to watch your video.",
          icon: require("@/components/SuperApp/DialogImg/video.svg"),
          image: require("@/components/SuperApp/DialogImg/video.svg"),
          tags: ["Video awareness", "Retention", "Story views"],
        },
      ],
    };
  },
  computed: {
    currentObjective() {
      return this.objectives.find((o) => o.value === this.selectedObjective);
    },
    hasSelectedAccounts() {
      return Object.values(this.selectedAccounts).some((account) => !!account);
    },
    currentObjective() {
      return this.objectives.find((o) => o.value === this.selectedObjective);
    },
  },
  watch: {
    selectedAccounts: {
      deep: true,
      handler(val) {
        localStorage.setItem("superselected_accounts", JSON.stringify(val));
      },
    },
    selectedObjective(val) {
      localStorage.setItem("superselected_objective", val);
    },
  },
  mounted() {
    this.fetchPlatforms();

    // Load from localStorage
    const savedAccounts = localStorage.getItem("superselected_accounts");
    if (savedAccounts) {
      try {
        this.selectedAccounts = JSON.parse(savedAccounts);
        this.store.selectedAccounts = JSON.parse(savedAccounts);
      } catch (e) {
        console.warn("⚠️ Invalid localStorage for accounts");
      }
    }

    const savedObjective = localStorage.getItem("superselected_objective");
    if (savedObjective) {
      this.selectedObjective = savedObjective;
    }
  },
  methods: {
    async fetchPlatforms() {
      this.loading = true;
      try {
        const response = await apiClient.get("/super-ad/platforms-account");
        const accounts = response.data.data.data;

        const platformsMap = {};
        const platformLabels = {
          facebook: "Meta",
          snapchat: "Snapchat account",
          tiktok: "Tik Tok account",
        };

        for (const acc of accounts) {
          const provider = acc.provider.toLowerCase();
          if (!platformsMap[provider]) {
            platformsMap[provider] = {
              name: provider,
              label: platformLabels[provider] || provider,
              icon: this.getPlatformIcon(provider),
              accounts: [],
            };
          }

          platformsMap[provider].accounts.push({
            id: acc.id,
            name: acc.account_name,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(
              acc.account_name
            )}&background=random`,
          });
        }

        this.platforms = Object.values(platformsMap);
        this.platforms.forEach((platform) => {
          this.selectedAccounts[platform.name] = null;
        });
      } catch (error) {
        console.error("❌ Error fetching accounts:", error);
      } finally {
        this.loading = false;
      }
    },
    getPlatformIcon(provider) {
      switch (provider) {
        case "facebook":
          return require("@/assets/images/icons/meta.png");
        case "snapchat":
          return require("@/assets/images/icons/snap.png");
        case "tiktok":
          return require("@/assets/images/icons/tiktok.png");
        default:
          return "";
      }
    },
    capitalize(word) {
      if (!word) return "";
      return word.charAt(0).toUpperCase() + word.slice(1);
    },
    goToStep2() {
      const hasSelected = Object.values(this.selectedAccounts).some(
        (account) => !!account
      );

      if (!hasSelected) {
        this.$toast?.warning?.("Please select at least one account.");
        return;
      }

      this.step1 = false;
      this.step2 = true;

      const currencyMap = {
        tiktok: "USD",
        snapchat: "USD",
        facebook: "USD",
      };

      const iconMap = {
        facebook: new URL("@/assets/images/icons/meta.png", import.meta.url)
          .href,
        snapchat: new URL("@/assets/images/icons/snap.png", import.meta.url)
          .href,
        tiktok: new URL("@/assets/images/icons/tiktok.png", import.meta.url)
          .href,
      };

      this.selectedPlatforms = [];

      for (const platformKey in this.selectedAccounts) {
        if (this.selectedAccounts[platformKey]) {
          this.selectedPlatforms.push({
            name:
              platformKey === "facebook"
                ? "Meta"
                : this.capitalize(platformKey),
            icon: iconMap[platformKey],
            budget: "",
            currency: currencyMap[platformKey] || "USD",
            budgetType: "daily",
          });
        }
      }

      // Store in localStorage
      localStorage.setItem(
        "super_selected_platforms",
        JSON.stringify(this.selectedPlatforms)
      );
      this.store.platforms = this.selectedPlatforms;
    },
    finalSubmit() {
      this.step2 = false;
      this.store.campaignObjective = this.selectedObjective;

    },
    clearaccount() {
      this.platforms.forEach((platform) => {
        this.selectedAccounts[platform.name] = null;
      });
      localStorage.removeItem("superselected_accounts");
    },
  },
};
</script>

<style scoped>
.v-card-text {
  max-height: 60vh;
  overflow-y: auto;
}

.as {
  padding: 0.8rem;
  background-color: #f2f2f299;
  border-radius: 0.5rem;
  margin: 0 !important;
}

.text-subtitle-2 {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #202224;
}

.con,
.can {
  border-radius: 8px;
  text-transform: capitalize;
  width: 7rem;
}
</style>
